import { api } from "./client";
import type { ApiResponse } from "./types";

// ============================================================================
// The member's own view of themselves — and the two people-discovery lists.
//
// Worth knowing about the two discovery calls: `nearby` and `from-my-state` are
// NOT the Identity Board. The board matches on who somebody IS, scored across
// dozens of dimensions; these two answer a much plainer question — who else is
// physically close, and who else came from the same place. They exist because
// those are the first two questions a person actually asks, and neither needs a
// completed board to answer.
//
// "Near me" is a METRO, not a radius. That is a measured choice, not a
// shortcut: zero seeded providers carry coordinates, and when the obvious
// distance rule was tested it failed — San Francisco is 42 miles from San Jose
// and inside the hour, Berkeley is 42.5 and outside it, because the bay is in
// the way. Drive time is the real rule and it needs a routing service.
// ============================================================================

/** GET /users/me/completeness, as the API sends it (the spec's ProfileCompleteness). */
export interface ProfileCompleteness {
  percentage: number;
  /** Field names still empty, in the order worth asking. */
  missing: string[];
  /** What to do next, as sentences. */
  suggestions: string[];
}

export interface NearbyUser {
  id: string;
  name: string;
  avatarUrl: string | null;
  city: string | null;
  originState: string | null;
}

export interface FollowedOrganizer {
  id: string;
  displayName: string;
  avatarUrl: string | null;
  isVerified: boolean;
  upcomingEventCount: number;
}

export interface WatchlistEntry {
  eventId: string;
  addedAt: string;
  event: { id: string; title: string; eventDate: string; city: string; imageUrl: string | null };
}

export interface OrderSummary {
  id: string;
  status: string;
  createdAt: string;
  totalCents: number;
  event: { id: string; title: string; eventDate: string };
  ticketCount: number;
}

/** How complete the profile is, and what would move it most. */
export function getMyCompleteness() {
  return api.get<ApiResponse<ProfileCompleteness>>("/users/me/completeness");
}

/** Members in the same metro. See the note above — metro, not radius. */
export function getNearbyUsers(params?: { limit?: number; offset?: number }) {
  return api.get<ApiResponse<{ users: NearbyUser[] }>>(
    "/users/nearby",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/** Members who share your origin state, wherever they now live. */
export function getUsersFromMyState(params?: { limit?: number; offset?: number }) {
  return api.get<ApiResponse<{ users: NearbyUser[] }>>(
    "/users/from-my-state",
    params as Record<string, string | number | boolean | undefined>,
  );
}

/** Organisers you follow — the fan-out list when one of them publishes. */
export function getMyFollowedOrganizers() {
  return api.get<ApiResponse<{ organizers: FollowedOrganizer[] }>>(
    "/users/me/followed-organizers",
  );
}

/** Events you saved without committing. Distinct from an RSVP on purpose. */
export function getMyWatchlist() {
  return api.get<ApiResponse<{ watchlist: WatchlistEntry[] }>>("/users/me/watchlist");
}

/** `eventId` narrows the list; it is not an identity. Recover one checkout by retrying its key. */
export function getMyOrders(params?: { eventId?: string }) {
  return api.get<ApiResponse<{ orders: OrderSummary[] }>>("/users/me/orders", params as Record<string, string | undefined>);
}

export type ContributionSurface = 'curious' | 'awaaz' | 'yaadein';
export interface ProfileContribution {
  id: string;
  kind: string;
  title: string;
  createdAt: string;
  target: { kind: string; id: string };
}

export function getProfileContributions(memberId: string, surface: ContributionSurface, cursor?: string) {
  return api.get<ApiResponse<{ items: ProfileContribution[]; nextCursor: string | null }>>(
    `/users/${memberId}/contributions`, { surface, cursor, limit: 20 },
  );
}

/**
 * A member's Parichay as the caller sees it (GET /users/{id}). Only the parts
 * the Lab shows are typed here.
 */
export interface Parichay {
  id: string;
  name: string;
  visibility: { state: string; message: string | null };
  /** One fixed line, only when the member turned it on. Absent when restricted. */
  publicHousehold?: { label: string } | null;
  /** Absent when restricted. A part the member keeps from me is empty or null here, never flagged. */
  identity?: {
    calledName: string;
    declaration: { plates: string[]; line: string } | null;
    roots: Array<{ code: string | null; name: string; place: string | null }>;
    /** Every place they grew up; `raisedIn` is the first. */
    grewUp: Array<{ country: string; region: string | null; place: string | null }>;
    languages: Array<{ code: string; name: string }>;
  };
}

export function getParichay(memberId: string) {
  return api.get<ApiResponse<Parichay>>(`/users/${memberId}`);
}

/** GET /users/me/near-counts: how many members near me share each answer (under 3 left out). */
export interface NearCounts {
  topic: string;
  place: string | null;
  floor: number;
  mine: string | null;
  counts: Array<{ value: string; label: string; count: number; sentence: string }>;
}

export type NearCountTopic = "work" | "roots";

export function getNearCounts(topic: NearCountTopic) {
  return api.get<ApiResponse<NearCounts>>("/users/me/near-counts", { topic });
}

/** Who sees each part of my identity (GET /users/me `visibility`, PATCH /users/me). */
export type PartVisibility = "everyone" | "connections" | "only_me";
export interface PartVisibilitySet {
  roots: PartVisibility;
  grewUp: PartVisibility;
  languages: PartVisibility;
}

export interface MyDeclaration {
  calledName: string;
  visibility: PartVisibilitySet;
  declaration: {
    choice: string | null;
    plates: string[];
    line: string | null;
    /** "One of 40 Punjabis on WeDesi · 9 who grew up in Ahmedabad", or null under the floor. */
    count: { label: string } | null;
  };
}

export function getMyDeclaration() {
  return api.get<ApiResponse<MyDeclaration>>("/users/me");
}

export function updateMe(body: { calledName?: string | null; visibility?: Partial<PartVisibilitySet> }) {
  return api.patch<ApiResponse<unknown>>("/users/me", body);
}

/** GET /users/me/journey-help: visa, tax and legal help near me, chosen from my journey answers. */
export interface JourneyHelp {
  answered: boolean;
  place: string | null;
  sections: Array<{
    key: string;
    title: string;
    providers: Array<{ id: string; name: string; isVerified: boolean; city: string | null; category: { code: string; name: string } }>;
  }>;
  events: Array<{ id: string; title: string; eventDate: string; city: string | null }>;
}

export function getJourneyHelp() {
  return api.get<ApiResponse<JourneyHelp>>("/users/me/journey-help");
}

/** The line under a name, as the server composes it (shared/identity/declare.ts). */
export interface IdentityDeclaration {
  plates: string[];
  line: string;
}

export interface IdentityPeopleName {
  id: string;
  singular: string;
  plural: string;
  kind: string;
  states: string[];
  language: string | null;
}

export interface DeclarationOptions {
  default: IdentityDeclaration | null;
  options: { id: string; line: string }[];
  peopleNames: IdentityPeopleName[];
}

/** POST /identity/declaration/options: every way the answers can be said. Saves nothing. */
export function getDeclarationOptions(body: {
  roots: unknown;
  grewUp?: unknown;
  peopleName?: string[];
  motherTongue?: string | null;
}) {
  return api.post<ApiResponse<DeclarationOptions>>("/identity/declaration/options", body);
}

/** GET /reference/people-names: the reviewed people-names, all or one state's. */
export function getPeopleNames(state?: string) {
  return api.get<ApiResponse<{ version: string; owner: string; items: IdentityPeopleName[] }>>(
    "/reference/people-names",
    state ? { state } : undefined,
  );
}
